import { AxiosResponse } from 'axios'

import ClientSDK from '../../../App/ClientSDK'
import { formatDate } from '../../../App/Util'

import { IAbsenceRequest, IApiAbsenceRequest } from './IAbsenceRequest'
import {
  IAbsenceRequestStatistics,
  IApiAbsenceRequestStatistics,
} from './IAbsenceRequestStatistics'
import Transformer from './Transformer'

// todo: change new naming in endpoint. Endpoints expects ends_at + starts_at
// todo: add Redux call that includes dates when other PR is merged and new datepicker is also available
const baseUrl = `hr/absence-requests`

class Client {
  static getAbsenceRequests = (activeUser: {
    uuid: string
    firstName: string
    lastName: string
  } | undefined): Promise<IAbsenceRequest[]> => {
    return ClientSDK.instance()
      .get(baseUrl)
      .then(
        (
          response: AxiosResponse<{ list: IApiAbsenceRequest[] }>,
        ): IAbsenceRequest[] => {
          const AbsenceList = response.data.list.map((data: IApiAbsenceRequest) =>
          Transformer.transform(data))

          const filterByActiveUser = (item: IAbsenceRequest) => {
            if (!activeUser || item.person.uuid === activeUser.uuid) {
              return true
            }

            return false;
          }

          return AbsenceList.filter(filterByActiveUser)
        },
      )
  }

  static createAbsenceRequest = (
    absenceRequest: IAbsenceRequest,
  ): Promise<IAbsenceRequest> => {
    const startDate = formatDate(absenceRequest.startsAt, 'FORMAT_DATE_API')
    const startTime = formatDate(absenceRequest.startsAt, 'FORMAT_TIME_API')
    const endsDate = formatDate(absenceRequest.endsAt, 'FORMAT_DATE_API')
    const endsTime = formatDate(absenceRequest.endsAt, 'FORMAT_TIME_API')

    return ClientSDK.instance()
      .post(baseUrl, {
        person_uuid: absenceRequest.person.uuid,
        starts_at: `${startDate} ${startTime}`,
        ends_at: `${endsDate} ${endsTime}`,
        type: absenceRequest.type,
        reason: absenceRequest.reason,
      })
      .then((response: AxiosResponse<{ id: string }>): IAbsenceRequest => {
        if (response.data.id) {
          absenceRequest.id = response.data.id
          absenceRequest.startsAt = absenceRequest.startsAt.seconds(0)
          absenceRequest.startsAt = absenceRequest.endsAt.seconds(0)
          absenceRequest.status = 'PENDING'

          return absenceRequest
        }
      })
  }

  static deleteAbsenceRequest = (
    absenceRequest: IAbsenceRequest,
  ): Promise<AxiosResponse> =>
    ClientSDK.instance().delete(`${baseUrl}/${absenceRequest.id}`)

  static updateAbsenceRequest = (
    absenceRequest: IAbsenceRequest,
  ): Promise<IAbsenceRequest> => {
    const startDate = formatDate(absenceRequest.startsAt, 'FORMAT_DATE_API')
    const startTime = formatDate(absenceRequest.startsAt, 'FORMAT_TIME_API')
    const endsDate = formatDate(absenceRequest.endsAt, 'FORMAT_DATE_API')
    const endsTime = formatDate(absenceRequest.endsAt, 'FORMAT_TIME_API')

    return ClientSDK.instance()
      .patch(`${baseUrl}/${absenceRequest.id}`, {
        // note: person cannot be changed
        starts_at: `${startDate} ${startTime}`,
        ends_at: `${endsDate} ${endsTime}`,
        type: absenceRequest.type,
        reason: absenceRequest.reason,
      })
      .then(() => {
        absenceRequest.status = 'PENDING'

        return absenceRequest
      })
  }

  static approveAbsenceRequest = (
    absenceRequest: IAbsenceRequest,
  ): Promise<IAbsenceRequest> => {
    return ClientSDK.instance()
      .post(`${baseUrl}/${absenceRequest.id}/approve`, {
        review_reason: absenceRequest.reviewReason,
      })
      .then((response: AxiosResponse) => {
        if ('success' === response.data.status) {
          absenceRequest.status = 'APPROVED'

          return absenceRequest
        }
      })
  }

  static declineAbsenceRequest = (
    absenceRequest: IAbsenceRequest,
  ): Promise<IAbsenceRequest> => {
    return ClientSDK.instance()
      .post(`${baseUrl}/${absenceRequest.id}/decline`, {
        review_reason: absenceRequest.reviewReason,
      })
      .then((response: AxiosResponse) => {
        if ('success' === response.data.status) {
          absenceRequest.status = 'DECLINED'

          return absenceRequest
        }
      })
  }

  static getAbsenceStatistics = (): Promise<IAbsenceRequestStatistics[]> => {
    return ClientSDK.instance()
      .get(`${baseUrl}/statistics`)
      .then(
        (
          response: AxiosResponse<IApiAbsenceRequestStatistics[]>,
        ): IAbsenceRequestStatistics[] => {
          return response.data.map(stat => Transformer.transformStatistic(stat))
        },
      )
  }
}

export default Client
